import type {
  AudioTrackSummary,
  MusicOnHoldApi,
  Playlist,
  PlaylistListItem,
} from './musicOnHoldApi.types'

export const MOCK_AUDIO_TRACKS: AudioTrackSummary[] = [
  {
    id: 101,
    name: 'Gentle Piano Loop',
    mp3_file_name: 'gentle_piano_loop.mp3',
    location: '/sounds/hold/gentle_piano_loop.mp3',
    pbx_id: 1,
  },
  {
    id: 102,
    name: 'Smooth Jazz',
    mp3_file_name: 'smooth_jazz.mp3',
    location: '/sounds/hold/smooth_jazz.mp3',
    pbx_id: 1,
  },
  {
    id: 103,
    name: 'Acoustic Guitar',
    mp3_file_name: 'acoustic_guitar.mp3',
    location: '/sounds/hold/acoustic_guitar.mp3',
    pbx_id: 1,
  },
  {
    id: 104,
    name: 'Classical Strings',
    mp3_file_name: 'classical_strings.mp3',
    location: '/sounds/hold/classical_strings.mp3',
    pbx_id: 1,
  },
  {
    id: 105,
    name: 'Lo-Fi Beats',
    mp3_file_name: 'lofi_beats.mp3',
    location: '/sounds/hold/lofi_beats.mp3',
    pbx_id: 1,
  },
]

export const MOCK_PLAYLISTS: Playlist[] = [
  {
    id: 1,
    name: 'Default System Playlist',
    type: 'list',
    active: true,
    system: 1,
    url: null,
    is_default: true,
    audio_tracks: [MOCK_AUDIO_TRACKS[0], MOCK_AUDIO_TRACKS[1]],
    created_at: '2024-06-15T08:00:00Z',
    updated_at: '2025-01-10T14:30:00Z',
  },
  {
    id: 2,
    name: 'Jazz Lounge',
    type: 'list',
    active: true,
    system: 0,
    url: null,
    is_default: false,
    audio_tracks: [
      MOCK_AUDIO_TRACKS[1],
      MOCK_AUDIO_TRACKS[2],
      MOCK_AUDIO_TRACKS[4],
    ],
    created_at: '2025-02-20T10:00:00Z',
    updated_at: '2025-03-01T09:15:00Z',
  },
  {
    id: 3,
    name: 'Classical',
    type: 'list',
    active: true,
    system: 0,
    url: null,
    is_default: false,
    audio_tracks: [MOCK_AUDIO_TRACKS[3]],
    created_at: '2025-03-01T12:00:00Z',
    updated_at: '2025-03-01T12:00:00Z',
  },
  {
    id: 5,
    name: 'Empty Playlist',
    type: 'list',
    active: false,
    system: 0,
    url: null,
    is_default: false,
    audio_tracks: [],
    created_at: '2025-03-05T09:00:00Z',
    updated_at: '2025-03-05T09:00:00Z',
  },
]

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms))

export const createMockMusicOnHoldApi = (): MusicOnHoldApi => {
  let playlists = [...MOCK_PLAYLISTS]
  let audioTracks = [...MOCK_AUDIO_TRACKS]

  return {
    async getPlaylists(): Promise<PlaylistListItem[]> {
      await delay(400)
      return playlists.map((p) => ({
        id: p.id,
        name: p.name,
        type: p.type,
        url: p.url,
        system: p.system,
        active: p.active,
        is_default: p.is_default,
        track_count: (p.audio_tracks ?? []).length,
        updated_at: p.updated_at,
      }))
    },

    async getPlaylist(playlistId: number) {
      await delay(300)
      const p = playlists.find((pl) => pl.id === playlistId)
      if (!p) throw new Error(`Playlist ${playlistId} not found`)
      return { ...p }
    },

    async createPlaylist(payload) {
      await delay(500)
      const newPlaylist: Playlist = {
        id: Math.max(...playlists.map((p) => p.id)) + 1,
        name: payload.name,
        type: payload.type,
        active: true,
        system: 0,
        url: null,
        is_default: false,
        audio_tracks: [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }
      playlists = [newPlaylist, ...playlists]
      return newPlaylist
    },

    async updatePlaylist(id, payload) {
      await delay(400)
      const idx = playlists.findIndex((p) => p.id === id)
      if (idx === -1) throw new Error(`Playlist ${id} not found`)
      playlists[idx] = {
        ...playlists[idx],
        ...payload,
        updated_at: new Date().toISOString(),
      }
      return playlists[idx]
    },

    async deletePlaylist(id) {
      await delay(400)
      playlists = playlists.filter((p) => p.id !== id)
    },

    async setDefaultPlaylist(_pbxId, playlistId) {
      await delay(300)
      playlists = playlists.map((p) => ({
        ...p,
        is_default: p.id === playlistId,
      }))
    },

    async getAudioTracks() {
      await delay(300)
      return [...audioTracks]
    },

    async getAudioTrack(_pbxId, uuid) {
      await delay(200)
      const track = audioTracks.find((t) => String(t.id) === uuid)
      if (!track) throw new Error(`Audio track ${uuid} not found`)
      return {
        ...track,
        created_at: '2025-01-01T00:00:00Z',
        updated_at: '2025-01-01T00:00:00Z',
        original_file_name: track.mp3_file_name,
        phone_server_path: track.location,
        uuid: String(track.id),
        file_url: `https://s3.example.com${track.location}`,
      }
    },

    async createAudioTrack(payload) {
      await delay(600)
      const slug = payload.name.toLowerCase().replace(/\s+/g, '_')
      const newTrack = {
        id: Math.max(...audioTracks.map((t) => t.id)) + 1,
        name: payload.name,
        mp3_file_name: `${slug}.mp3`,
        location: `/sounds/hold/${slug}.mp3`,
        pbx_id: payload.pbx_id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        original_file_name: 'uploaded_file.mp3',
        phone_server_path: '',
        uuid: String(Math.max(...audioTracks.map((t) => t.id)) + 1),
        file_url: `https://s3.example.com/sounds/hold/${slug}.mp3`,
      }
      audioTracks = [newTrack, ...audioTracks]
      return newTrack
    },

    async updateAudioTrack(_pbxId, uuid, payload) {
      await delay(400)
      const idx = audioTracks.findIndex((t) => String(t.id) === uuid)
      if (idx === -1) throw new Error(`Audio track ${uuid} not found`)
      if (payload.name !== undefined) {
        audioTracks[idx] = { ...audioTracks[idx], name: payload.name }
      }
      return {
        ...audioTracks[idx],
        created_at: '2025-01-01T00:00:00Z',
        updated_at: new Date().toISOString(),
        original_file_name: audioTracks[idx].mp3_file_name,
        phone_server_path: audioTracks[idx].location,
        uuid: String(audioTracks[idx].id),
        file_url: `https://s3.example.com${audioTracks[idx].location}`,
      }
    },

    async deleteAudioTrack(_pbxId, uuid) {
      await delay(300)
      audioTracks = audioTracks.filter((t) => String(t.id) !== uuid)
    },

    async addTrackToPlaylist(playlistId, audioTrackId) {
      await delay(300)
      const pIdx = playlists.findIndex((p) => p.id === playlistId)
      const track = audioTracks.find((t) => t.id === audioTrackId)
      if (pIdx === -1 || !track) throw new Error('Not found')
      const tracks = playlists[pIdx].audio_tracks ?? []
      if (!tracks.find((t) => t.id === audioTrackId)) {
        playlists[pIdx] = {
          ...playlists[pIdx],
          audio_tracks: [...tracks, track],
        }
      }
    },

    async removeTrackFromPlaylist(playlistId, audioTrackId) {
      await delay(300)
      const pIdx = playlists.findIndex((p) => p.id === playlistId)
      if (pIdx === -1) throw new Error('Not found')
      playlists[pIdx] = {
        ...playlists[pIdx],
        audio_tracks: (playlists[pIdx].audio_tracks ?? []).filter(
          (t) => t.id !== audioTrackId,
        ),
      }
    },
  }
}

/** @deprecated Use createMockMusicOnHoldApi() for stateful mocks */
export const mockMusicOnHoldApi = createMockMusicOnHoldApi()
